import { Alert, Box, Button, Stack, TextField } from '@mui/material';
import PageContainer from '../layout/PageContainer';
import { useState, SubmitEvent, useCallback, useEffect, ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleReCaptcha, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/axiosInstance';
import useNotifications from '../../hooks/useNotifications/useNotifications';
import { emailRegex } from '../../utilities/EmailRegex';

export default function Login() {
  const navigate = useNavigate();

  const { user, login, loading } = useAuth();

  const notifications = useNotifications();

  const { executeRecaptcha } = useGoogleReCaptcha();

  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<any>({});
  const [emailInvalid, setEmailInvalid] = useState(true);
  const [loginUnsuccessfulReason, setLoginUnsuccessfulReason] = useState('');
  const [validatingEmail, setValidatingEmail] = useState(false);
  const [emailValidated, setEmailValidated] = useState(false);
  const [emailNotValid, setEmailNotValid] = useState(false);
  const [reCaptchaProcessed, setReCaptchaProcessed] = useState(false);
  const [reCaptchaInvalid, setReCaptchaInvalid] = useState(false);
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [navigate, user]);

  const onReCaptchaVerify = useCallback((value: string) => {
    setReCaptchaProcessed(true);

    if (!value) {
      setReCaptchaInvalid(true);
    }
  }, []);

  const sendEmailValidationEmail = useCallback(async () => {
    setEmailValidated(false);
    setEmailNotValid(false);

    if (reCaptchaProcessed &&
      !reCaptchaInvalid &&
      email) {

      setValidatingEmail(true);

      try {
        const { data: result } = await api.post(
          '/send-email-validation-email',
          {
            email: email
          });

        setValidatingEmail(false);

        if (result) {
          setEmailValidated(true);
        } else {
          setEmailNotValid(true);
        }
      }
      catch (error: any) {
        if (error.response?.data?.errors) {
          setErrors(error.response.data.errors);
        } else {
          notifications.show(
            'Unknown error.', {
            severity: 'error'
          });
        }
      }

      setReCaptchaProcessed(false);
      setReCaptchaInvalid(false);
      setRefreshReCaptcha(!refreshReCaptcha);
    }
  }, [notifications, reCaptchaInvalid, reCaptchaProcessed, refreshReCaptcha, email]);

  const onEmailChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const email = event.target.value;

    setEmail(email);

    setEmailInvalid(email.length <= 0 || !emailRegex.test(email));
  }, []);

  const handleLogin = async (event: SubmitEvent) => {
    event.preventDefault();

    if (reCaptchaProcessed &&
      !reCaptchaInvalid &&
      email) {

      if (!executeRecaptcha) {
        setErrors('Unable to verify reCAPTCHA');
        return;
      }

      const recaptchaToken = await executeRecaptcha('login');

      try {
        await login(email, recaptchaToken);
      }
      catch (error: any) {
        if (error.response?.data?.errors) {
          setErrors(error.response.data.errors);
        } else {
          setLoginUnsuccessfulReason(error.message);
        }
      }

      setReCaptchaProcessed(false);
      setReCaptchaInvalid(false);
      setRefreshReCaptcha(!refreshReCaptcha);
    }
  };

  return <PageContainer
    title="Login"
  >
    <Box
      component="form"
      onSubmit={handleLogin}
      noValidate
      autoComplete="off"
      sx={{ width: '100%', maxWidth: '500px' }}
    >
      <Box sx={{ width: '100%' }}>
        {loginUnsuccessfulReason ?
          <Alert severity="error">
            {loginUnsuccessfulReason}
          </Alert> :
          <></>
        }
        {reCaptchaProcessed && reCaptchaInvalid ?
          <Alert severity="error">
            reCAPTCHA failed.
          </Alert> :
          <></>
        }
        {emailValidated ?
          <Alert severity="success">
            An email has been sent to the specified email address. Please click the link in the email to create an additional passkey.
          </Alert> :
          <></>
        }
        {emailNotValid ?
          <Alert severity="error">
            Email address not valid.
          </Alert> :
          <></>
        }
        <Stack direction="column" spacing={2} sx={{ my: 2 }}>
          <TextField
            type="email"
            autoComplete="email"
            value={email}
            onChange={onEmailChange}
            name="email"
            label="Email"
            variant="outlined"
            error={errors.Email}
            helperText={errors.Email ? errors.Email[0] : ' '}
            slotProps={{ inputLabel: { shrink: true } }}
            required
            fullWidth
          />
        </Stack>
        <Box sx={{ my: 2 }}>
          <GoogleReCaptcha
            onVerify={onReCaptchaVerify}
            refreshReCaptcha={refreshReCaptcha}
          />
        </Box>
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <Button
            variant="contained"
            disabled={loading || validatingEmail}
            onClick={(event) => navigate('/register')}
          >
            Register
          </Button>
          <Button
            variant="contained"
            disabled={loading || emailInvalid || !reCaptchaProcessed || reCaptchaInvalid}
            onClick={sendEmailValidationEmail}
            loading={validatingEmail}
          >
            Create Add'l Passkey
          </Button>
          <Button
            type="submit"
            variant="contained"
            size="large"
            color="success"
            disabled={validatingEmail || emailInvalid || !reCaptchaProcessed || reCaptchaInvalid}
            loading={loading}
          >
            Login
          </Button>
        </Stack>
      </Box>
    </Box>
  </PageContainer>
}
